import { useQuery } from '@tanstack/react-query';
import { AxiosMethods } from '@setvi/shared/enums';
import { axiosHelper } from '@setvi/shared/services';

import type { CoverDefaults, CoverForm } from '../types';
import { buildCoverFieldsBody } from '../utils';

enum CoverTemplateQueryKey {
  List = 'print_order_cover_templates'
}

// v2.0 cover-page endpoint. Hits ADMIN_API_BASE_URL
// (https://devapi.setvi.com in develop). The resource id is appended.
const COVER_PAGE_ENDPOINT = '/api/v2.0/ShoppingCart/coverpage';

// v2.0 dynamic cover-page generation endpoint (renders the live preview).
const DYNAMIC_COVER_PAGE_ENDPOINT = '/api/v2.0/ShoppingCart/dynamiccoverpage';

/**
 * A cover page. `Url` is its PDF, whose first page is the grid thumbnail and
 * which the live preview renders; `FileName` identifies it in every request.
 */
export interface CoverPage {
  Brand: string;
  FileName: string;
  Url: string;
}

interface CoverPageApiResponse {
  Status: string;
  Data: {
    ResourceId: number;
    HasCoverPage: boolean;
    Available: boolean;
    Message: string | null;
    CoverPages: CoverPage[] | null;
    Defaults: CoverDefaults | null;
  } | null;
  Error: string | null;
}

const getCoverTemplatesQuery = (resourceId: number) => ({
  queryKey: [CoverTemplateQueryKey.List, resourceId],
  queryFn: async () => {
    const response: CoverPageApiResponse = await axiosHelper({
      endpoint: `${COVER_PAGE_ENDPOINT}/${resourceId}`,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    });
    return response?.Data ?? null;
  }
});

/**
 * Fetch the cover pages and the form's pre-fill values for a resource. Only
 * runs for resources that support a cover page (`enabled`).
 */
export const useCoverTemplates = (resourceId: number, enabled: boolean) => {
  const { data, isLoading } = useQuery({
    ...getCoverTemplatesQuery(resourceId),
    enabled: enabled && !!resourceId
  });
  return {
    templates: data?.CoverPages ?? [],
    defaults: data?.Defaults,
    // Only an explicit false hides the option, so a response without the field
    // keeps the cover pages visible.
    available: data?.Available !== false,
    message: data?.Message ?? null,
    loading: isLoading
  };
};

// Generated dynamic cover-page preview — a single combined PDF (cover +
// resource). `DownloadURLWithSas` drives the preview; `FileName` is the
// generated name sent on save (DynamicPdfFileName).
export interface DynamicCoverPageData {
  ResourceId: number;
  CoverPageName: string | null;
  FileName: string;
  BlobPath: string | null;
  DownloadURL: string | null;
  DownloadURLWithSas: string | null;
  PageCount: number | null;
  CoverPageCount: number | null;
  ResourcePageCount: number | null;
  SizeBytes: number | null;
}

interface DynamicCoverPageApiResponse {
  Status: string;
  Data: DynamicCoverPageData | null;
  Error: { Id: number; Message: string } | string | null;
}

export interface GenerateDynamicCoverPageInput {
  resourceId: number;
  coverPageName: string;
  /** Cover details the generated cover is stamped with. */
  fields?: CoverForm;
}

/**
 * Generate the dynamic cover-page preview for a resource + selected cover page
 * name. Returns a single PDF (DownloadURLWithSas) used for the live preview.
 */
const generateDynamicCoverPage = async ({
  resourceId,
  coverPageName,
  fields
}: GenerateDynamicCoverPageInput): Promise<DynamicCoverPageData | null> => {
  const response: DynamicCoverPageApiResponse = await axiosHelper({
    endpoint: DYNAMIC_COVER_PAGE_ENDPOINT,
    method: AxiosMethods.POST,
    baseURL: process.env.ADMIN_API_BASE_URL,
    body: { resourceId, coverPageName, ...buildCoverFieldsBody(fields) }
  });
  return response?.Data ?? null;
};

// React Query mutation factory (used via useSubscribedMutation). Called when
// the user picks a cover page.
export const generateDynamicCoverPageMutation = () => ({
  mutationFn: generateDynamicCoverPage
});
